import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import salesService from '../services/salesService';
import { useNotification } from '../context/NotificationContext';
import AuthContext from '../context/AuthContext';
import { formatInstantDate } from '../utils/datetime';
import ConfirmActionModal from '../components/ConfirmActionModal';
import RegisterPaymentModal from '../components/RegisterPaymentModal';
import SortableHeader from '../components/SortableHeader';
import '../styles/general.css';

const SalesListPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { notify } = useNotification();
  const { timezone, canManage } = useContext(AuthContext);
  // Write actions are hidden, not disabled: a button the user can never
  // enable is its own kind of noise. The API rejects them regardless.
  const canWrite = canManage && canManage('sales');
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [sortBy, setSortBy] = useState('-created_at');
  const [filterField, setFilterField] = useState('');
  const [filterValue, setFilterValue] = useState('');
  const [createdFrom, setCreatedFrom] = useState('');
  const [createdTo, setCreatedTo] = useState('');
  const [cancelingSale, setCancelingSale] = useState(null);
  const [payingSale, setPayingSale] = useState(null);
  const [invoicingSale, setInvoicingSale] = useState(null);
  const [processing, setProcessing] = useState(false);

  const perPage = 25;
  const totalPages = Math.ceil(totalCount / perPage);

  const loadSales = useCallback(async () => {
    try {
      setLoading(true);
      const res = await salesService.listSales(
        filterField || null,
        filterValue || null,
        sortBy,
        page,
        createdFrom || null,
        createdTo || null
      );
      setSales(res.data.results || []);
      setTotalCount(res.data.count || 0);
    } catch (err) {
      console.error('Error loading Sales:', err);
      notify.error(t('sales.failedToLoadSales'));
    } finally {
      setLoading(false);
    }
  }, [page, sortBy, filterField, filterValue, createdFrom, createdTo, notify, t]);

  useEffect(() => {
    loadSales();
  }, [loadSales]);

  const handleFilterChange = (field, value) => {
    setFilterField(field);
    setFilterValue(value);
    setPage(1);
  };

  const handleCreatedFromChange = (value) => {
    setCreatedFrom(value);
    setPage(1);
  };

  const handleCreatedToChange = (value) => {
    setCreatedTo(value);
    setPage(1);
  };

  const handleSortChange = (field) => {
    if (sortBy === field) {
      setSortBy(`-${field}`);
    } else if (sortBy === `-${field}`) {
      setSortBy(field);
    } else {
      setSortBy(`-${field}`);
    }
    setPage(1);
  };

  const handleCancelConfirm = async () => {
    if (!cancelingSale) return;
    setProcessing(true);
    try {
      await salesService.cancelSale(cancelingSale.id);
      notify.success(t('sales.saleCancelled'));
      setCancelingSale(null);
      loadSales();
    } catch (err) {
      console.error('Error cancelling sale:', err);
      notify.error(t('sales.failedToCancelSale'));
    } finally {
      setProcessing(false);
    }
  };

  const handleMarkInvoicedConfirm = async () => {
    if (!invoicingSale) return;
    setProcessing(true);
    try {
      await salesService.markInvoiced(invoicingSale.id);
      notify.success(t('sales.saleInvoiced'));
      setInvoicingSale(null);
      loadSales();
    } catch (err) {
      console.error('Error marking sale as invoiced:', err);
      notify.error(t('sales.failedToMarkInvoiced'));
    } finally {
      setProcessing(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value || 0);
  };


  if (loading && sales.length === 0) {
    return (
      <div className="loading">
        {t('common.loading')}
      </div>
    );
  }

  return (
    <div className="page-container-wide">
      <div className="page-header">
        <h1>{t('sales.title')}</h1>
      </div>

      <div className="filter-panel">
        <div className="filter-row">
          <div className="filter-group">
            <label className="form-label">
              {t('filters.filterBy')}
            </label>
            <select
              value={filterField}
              onChange={(e) => handleFilterChange(e.target.value, filterValue)}
              className="form-select"
            >
              <option value="">{t('filters.none')}</option>
              <option value="sale_number">{t('sales.filters.saleNumber')}</option>
              <option value="client">{t('table.client')}</option>
              <option value="status">{t('sales.filters.statusLabel')}</option>
              <option value="client_tax_id">{t('sales.filters.rfc')}</option>
            </select>
          </div>

          <div className="filter-group">
            <label className="form-label">
              {t('filters.searchValue')}
            </label>
            {filterField === 'status' ? (
              <select
                value={filterValue}
                onChange={(e) => handleFilterChange(filterField, e.target.value)}
                className="form-select"
              >
                <option value="">{t('filters.none')}</option>
                <option value="pendiente">{t('sales.status.pendiente')}</option>
                <option value="pagado">{t('sales.status.pagado')}</option>
                <option value="cancelado">{t('sales.status.cancelado')}</option>
              </select>
            ) : (
              <input
                type="text"
                value={filterValue}
                onChange={(e) => handleFilterChange(filterField, e.target.value)}
                placeholder={t('filters.searchValue')}
                className="form-input"
              />
            )}
          </div>

          <div className="filter-group">
            <label className="form-label" htmlFor="salesCreatedFrom">
              {t('filters.createdFrom')}
            </label>
            <input
              id="salesCreatedFrom"
              type="date"
              value={createdFrom}
              onChange={(e) => handleCreatedFromChange(e.target.value)}
              className="form-input"
            />
          </div>

          <div className="filter-group">
            <label className="form-label" htmlFor="salesCreatedTo">
              {t('filters.createdTo')}
            </label>
            <input
              id="salesCreatedTo"
              type="date"
              value={createdTo}
              onChange={(e) => handleCreatedToChange(e.target.value)}
              className="form-input"
            />
          </div>
        </div>
      </div>

      <div className="table-wrapper">
        <table className="table">
          <thead>
            <tr>
              <SortableHeader field="sale_number" label={t('sales.saleNumber')} sortBy={sortBy} onSort={handleSortChange} />
              <th>{t('sales.filters.quoteNumber')}</th>
              <SortableHeader field="client" label={t('table.client')} sortBy={sortBy} onSort={handleSortChange} />
              <th>{t('table.buyer')}</th>
              <th className="text-center">{t('table.createdBy')}</th>
              <th className="text-center">{t('common.status')}</th>
              <th className="text-center">{t('table.date')}</th>
              <th className="text-center">{t('sales.invoiced')}</th>
              <SortableHeader field="total" label={t('table.total')} sortBy={sortBy} onSort={handleSortChange} className="text-right" />
              <th>{t('sales.account')}</th>
              <th className="text-center">{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {sales.length === 0 ? (
              <tr>
                <td colSpan="11" className="empty-state">
                  {t('sales.noSalesYet')}
                </td>
              </tr>
            ) : (
              sales.map((sale) => (
                <tr key={sale.id}>
                  <td>{sale.sale_number}</td>
                  <td>
                    {sale.quote_number ? (
                      <a
                        href={`/client-quotes/${sale.client_quote}/edit`}
                        onClick={(e) => { e.preventDefault(); navigate(`/client-quotes/${sale.client_quote}/edit`); }}
                      >
                        {sale.quote_number}
                      </a>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>{sale.client_name}</td>
                  <td>{sale.buyer_name}</td>
                  <td className="text-center">{sale.created_by_email}</td>
                  <td className="text-center">
                    <span className={`table-status ${sale.status}`}>
                      {t(`sales.status.${sale.status}`)}
                    </span>
                  </td>
                  <td className="text-center">{formatInstantDate(sale.created_at, timezone)}</td>
                  <td className="text-center">{sale.is_invoiced ? t('common.yes') : t('common.no')}</td>
                  <td className="text-right">{formatCurrency(sale.total)}</td>
                  {/* The dash covers two cases that look alike: a sale not
                      yet paid, and one paid before this column existed. */}
                  <td>{sale.monetary_account_name || '—'}</td>
                  <td className="text-center table-actions">
                    <button
                      onClick={() => navigate(`/sales/${sale.id}`)}
                      className="btn btn-secondary btn-small"
                    >
                      {t('sales.viewSale')}
                    </button>
                    {canWrite && !sale.is_invoiced && (
                      <button
                        onClick={() => setInvoicingSale(sale)}
                        className="btn btn-primary btn-small"
                      >
                        {t('sales.markInvoiced')}
                      </button>
                    )}
                    {sale.status === 'pendiente' && (
                      <button
                        onClick={() => setPayingSale(sale)}
                        className="btn btn-primary btn-small"
                      >
                        {t('sales.markPaid')}
                      </button>
                    )}
                    {sale.status !== 'cancelado' && (
                      <button
                        onClick={() => setCancelingSale(sale)}
                        className="btn btn-danger btn-small"
                      >
                        {t('sales.cancel')}
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page === 1}
            className="btn btn-primary"
          >
            {t('common.previous')}
          </button>
          <span className="pagination-info">
            {t('clientQuotes.pagination.pageOf', { page, total: totalPages })}
          </span>
          <button
            onClick={() => setPage(Math.min(totalPages, page + 1))}
            disabled={page === totalPages}
            className="btn btn-primary"
          >
            {t('common.next')}
          </button>
        </div>
      )}

      <ConfirmActionModal
        isOpen={!!cancelingSale}
        onClose={() => setCancelingSale(null)}
        onConfirm={handleCancelConfirm}
        title={t('sales.cancel')}
        message={t('sales.confirmCancel')}
        confirmLabel={t('common.confirm')}
        confirming={processing}
      />

      {/* A form rather than a confirmation: the payment now creates an
          accounting entry, which needs a destination and a date. */}
      <RegisterPaymentModal
        isOpen={!!payingSale}
        document={payingSale && {
          id: payingSale.id,
          number: payingSale.sale_number,
          total: payingSale.total
        }}
        onSubmit={(payload) => salesService.markSalePaid(payingSale.id, payload)}
        onClose={() => setPayingSale(null)}
        onPaid={() => {
          setPayingSale(null);
          loadSales();
        }}
      />

      <ConfirmActionModal
        isOpen={!!invoicingSale}
        onClose={() => setInvoicingSale(null)}
        onConfirm={handleMarkInvoicedConfirm}
        title={t('sales.markInvoiced')}
        message={t('sales.confirmMarkInvoiced')}
        confirmLabel={t('common.confirm')}
        confirming={processing}
      />
    </div>
  );
};

export default SalesListPage;
